import { CSSProperties, useRef, useState, useEffect, startTransition } from 'react';
import { Header, flexRender } from '@tanstack/react-table'; // Header 타입 추가
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/utils/tailwind';
import SortDefaultIcon from '@/assets/icons/OrderDetails/eva_repeat-fill.svg?react';
import SortAscIcon from '@/assets/icons/SortAscIcon.svg?react';
import SortDescIcon from '@/assets/icons/SortDescIcon.svg?react';
import { useSort } from '@/contexts/table-sort';

interface DraggableTableHeaderProps {
  header: Header<any, unknown>; // TanStack Table Header 타입
  disableDrag?: boolean;
}

interface SortingButtonProps {
  header: Header<any, unknown>; // DraggableTableHeaderProps에서 사용한 것과 동일한 타입
  charCount: number;
}

type SortColumnId = 'orderDate' | 'salesAmount' | 'quantity';

const SORT_COLUMN: Record<string, 'OrderDate' | 'SalesAmount' | 'OrderCount'> = {
  orderDate: 'OrderDate',
  salesAmount: 'SalesAmount',
  quantity: 'OrderCount',
};

const SortingButton = ({ header, charCount }: SortingButtonProps) => {
  const calculateLeftPosition = () => {
    return charCount * 10.75 + 15;
  };

  const { sort, setSort } = useSort();

  const targetColumn = SORT_COLUMN[header.column.id];

  const sortHandler = () => {
    const columnId = header.column.id;
    startTransition(() => {
      setSort(SORT_COLUMN[columnId as SortColumnId]);
    });
  };

  return (
    <button
      className={cn(
        'items-centertext-gr-400 absolute flex flex-col hover:text-gr-600',
        'top-1/2',
        '-translate-y-1/2',
      )}
      style={{
        left: `${calculateLeftPosition()}px`, // 이 부분만 style로 남겨야 함
      }}
    >
      {targetColumn &&
        (sort?.sortDirection === 'asc' ? (
          <SortAscIcon onClick={sortHandler} />
        ) : sort?.sortDirection === 'desc' ? (
          <SortDescIcon onClick={sortHandler} />
        ) : (
          <SortDefaultIcon onClick={sortHandler} />
        ))}
    </button>
  );
};

export const DraggableTableHeader = ({
  header,
  disableDrag = false,
}: DraggableTableHeaderProps) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: header.column.id,
    disabled: disableDrag,
  });

  const isCheckbox = header.column.id === 'checkbox';
  const isChecked = (header.column as any).getIsChecked?.();
  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: isCheckbox ? 'sticky' : 'relative', // 체크박스는 sticky
    left: isCheckbox ? 0 : 'auto', // 왼쪽에 고정
    transform: CSS.Translate.toString(transform),
    transition: 'width transform 0.2s',
    whiteSpace: 'nowrap',
    width: header.column.getSize(),
    zIndex: isCheckbox ? 50 : isDragging ? 30 : 10, // 체크박스 z-index 가장 높게
    background: isChecked ? '#EAF2FF' : isDragging ? '#F2F0FF' : 'transparent',
  };

  const textRef = useRef<HTMLDivElement>(null);
  const [charCount, setCharCount] = useState(0);

  // 글자 수 계산 (한글/영문 모두 1글자로 계산)
  useEffect(() => {
    if (textRef.current) {
      const text = textRef.current.textContent || '';
      setCharCount(text.length);
    }
  }, [header.column.columnDef.header]);

  return (
    <th
      ref={setNodeRef}
      colSpan={header.colSpan}
      style={style}
      className="h-[36px]  text-left Caption1M12"
    >
      <div className="flex items-center">
        {/* 헤더 텍스트 컨테이너 */}
        <div ref={textRef} className="relative flex-1" {...attributes} {...listeners}>
          {header.isPlaceholder
            ? null
            : flexRender(header.column.columnDef.header, header.getContext())}
        </div>

        {/* 정렬 버튼 (글자 수 기반 위치) */}
        {header.column.getCanSort() && <SortingButton header={header} charCount={charCount} />}
      </div>
    </th>
  );
};
